import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthUser, AuthTokens } from '@/types/auth'
import { post } from '@/lib/api-client'

/**
 * Authentication state interface
 */
interface AuthState {
  // State
  user: AuthUser | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  lastActivity: number | null

  // Actions
  setUser: (user: AuthUser | null) => void
  setTokens: (tokens: AuthTokens | null) => void
  login: (user: AuthUser, tokens: AuthTokens) => void
  logout: () => void
  updateLastActivity: () => void
  setLoading: (loading: boolean) => void
  isTokenExpired: () => boolean
  getAccessToken: () => string | null
}

/**
 * Authentication store with persistence
 * Manages user session, tokens, and authentication state
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: true,
      lastActivity: null,

      // Set the current user
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      // Set authentication tokens
      setTokens: (tokens) => set({ tokens }),

      // Login action - sets user and tokens together
      login: (user, tokens) =>
        set({
          user,
          tokens,
          isAuthenticated: true,
          isLoading: false,
          lastActivity: Date.now(),
        }),

      // Logout action - revokes refresh token then clears all auth state
      logout: () => {
        const { tokens } = get()
        // Fire-and-forget: revoke refresh token on the server
        if (tokens?.refreshToken) {
          post('/auth/logout', { refreshToken: tokens.refreshToken }).catch(() => {
            // Ignore errors - local logout still proceeds
          })
        }
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          lastActivity: null,
        })
      },

      // Update last activity timestamp for session timeout tracking
      updateLastActivity: () => set({ lastActivity: Date.now() }),

      // Set loading state
      setLoading: (isLoading) => set({ isLoading }),

      // Check if the current access token is expired
      // Uses 60-second buffer to allow refresh before actual expiration
      isTokenExpired: () => {
        const { tokens } = get()
        if (!tokens) return true
        // Consider token expired 60 seconds before actual expiration
        return Date.now() >= tokens.expiresAt - 60000
      },

      // Get the current access token (returns null if expired)
      getAccessToken: () => {
        const state = get()
        if (!state.tokens || state.isTokenExpired()) {
          return null
        }
        return state.tokens.accessToken
      },
    }),
    {
      name: 'pap-frontend-auth-storage',
      // Only persist user and tokens, not loading states
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        lastActivity: state.lastActivity,
      }),
    }
  )
)

// Export types for external use
export type { AuthState }
